import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formatMoney } from '../../util/currency';
import { calculateVATAmount, estimatedTotalPrice } from '../../util/booking';
import { types as sdkTypes } from '../../util/sdkLoader';
import { useConfiguration } from '../../context/configurationContext';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const VAT_PERCENTAGE = 20;

/**
 * Displays the VAT line item in the order breakdown for VAT registered users.
 *
 * The component calculates the VAT amount based on the customer's total price
 * and renders it as a separate line item in the breakdown.
 *
 * If the transaction is refunded, the VAT amount is displayed as 0.
 * If the user is not VAT registered, nothing is rendered.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {boolean} props.vatRegistered - Indicates whether VAT should be displayed.
 * @param {string} props.currency - Currency code used for the VAT price.
 * @param {boolean} props.refunded - Indicates whether the transaction was refunded.
 * @param {Array<Object>} props.txLineItems - Transaction line items used for calculating VAT.
 *
 * @returns {JSX.Element|null} VAT line item element or null if VAT is not applicable.
 */
const LineItemVATMaybe = props => {
  const { vatRegistered, currency, refunded, txLineItems } = props;
  const config = useConfiguration();
  const intl = useIntl();

  const customerLineItems = txLineItems.filter(item => item.includeFor.includes('customer'));

  const vatLabel = (
    <FormattedMessage id="OrderBreakdown.vat" values={{ percentage: VAT_PERCENTAGE }} />
  );
  const totalPriceCustomer = refunded
    ? new Money(0, config.currency)
    : estimatedTotalPrice(customerLineItems, currency);
  const vatAmount = calculateVATAmount(totalPriceCustomer.amount);

  const vatPrice = new Money(vatAmount, currency);

  if (!vatRegistered) return null;

  return (
    <div className={css.lineItemTotal}>
      <span className={css.itemLabel}>{vatLabel}</span>

      <span className={css.itemValue}>{formatMoney(intl, vatPrice)}</span>
    </div>
  );
};

export default LineItemVATMaybe;
